import {
  PRINT_FONT_SCALE_FACTOR,
  PRINT_FOOTER_MAX_CHARS,
  PRINT_FOOTER_MAX_LINES,
  PRINT_MARGIN_MAX_MM,
  PRINT_MARGIN_MIN_MM,
  PRINT_MIN_USABLE_HEIGHT_MM,
  PRINT_PAPER,
  PRINT_PAPER_SIZES_BY_DOCUMENT,
  PRINT_QR_SIZE_MM,
  type PrintDocumentType,
  type PrintTemplate,
  type PrintTemplateField,
  type PrintTemplateProblem,
} from '@optiflow/types';

/** The width content may use: the paper's physical width less its side margins. The one source of every printed width. */
export function usableWidthMm(
  template: Pick<PrintTemplate, 'paperSize' | 'marginLeftMm' | 'marginRightMm'>,
): number {
  return PRINT_PAPER[template.paperSize].widthMm - template.marginLeftMm - template.marginRightMm;
}

/** The QR's physical width for this paper and size preset; the quiet zone is inside the symbol, so this is all it needs. */
export function qrWidthMm(template: Pick<PrintTemplate, 'paperSize' | 'qrSize'>): number {
  return PRINT_QR_SIZE_MM[PRINT_PAPER[template.paperSize].kind][template.qrSize];
}

/** The base type size in CSS pixels after the font scale. */
export function printFontPx(template: Pick<PrintTemplate, 'paperSize' | 'fontScale'>): number {
  return (
    Math.round(
      PRINT_PAPER[template.paperSize].baseFontPx *
        PRINT_FONT_SCALE_FACTOR[template.fontScale] *
        100,
    ) / 100
  );
}

const MARGINS: PrintTemplateField[] = [
  'marginTopMm',
  'marginBottomMm',
  'marginLeftMm',
  'marginRightMm',
];
const TEXTS: PrintTemplateField[] = ['footerEn', 'footerAr', 'returnPolicyEn', 'returnPolicyAr'];

/** Anything that could be read as markup or hide text: angle brackets and control characters other than a line break. */
function hasMarkup(text: string): boolean {
  for (const character of text) {
    const code = character.charCodeAt(0);
    if (character === '<' || character === '>') return true;
    if ((code < 32 && code !== 10) || code === 127) return true;
  }
  return false;
}

/**
 * Why a template cannot print as a readable, verifiable document; empty when it can.
 *
 * Shared by the server, which is authoritative and refuses any problem, and the settings screen, which
 * shows the same problems before a save. Widths always come from the paper and its margins, never from
 * a separately stored width that could disagree with them.
 */
export function printTemplateProblems(
  documentType: PrintDocumentType,
  template: PrintTemplate,
): PrintTemplateProblem[] {
  const problems: PrintTemplateProblem[] = [];
  const paper = PRINT_PAPER[template.paperSize];

  if (!PRINT_PAPER_SIZES_BY_DOCUMENT[documentType].includes(template.paperSize)) {
    problems.push({ field: 'paperSize', code: 'PAPER_NOT_ALLOWED' });
  }

  for (const field of MARGINS) {
    const value = template[field] as number;
    if (!Number.isInteger(value) || value < PRINT_MARGIN_MIN_MM || value > PRINT_MARGIN_MAX_MM) {
      problems.push({ field, code: 'MARGIN_OUT_OF_RANGE' });
    }
  }

  const width = usableWidthMm(template);
  if (width < paper.minUsableWidthMm) {
    problems.push({ field: 'marginLeftMm', code: 'WIDTH_TOO_NARROW' });
  }
  if (
    paper.heightMm !== null &&
    paper.heightMm - template.marginTopMm - template.marginBottomMm < PRINT_MIN_USABLE_HEIGHT_MM
  ) {
    problems.push({ field: 'marginTopMm', code: 'HEIGHT_TOO_SHORT' });
  }
  if (qrWidthMm(template) > width) {
    problems.push({ field: 'qrSize', code: 'QR_DOES_NOT_FIT' });
  }

  for (const field of TEXTS) {
    const text = template[field] as string;
    if (text.length > PRINT_FOOTER_MAX_CHARS) problems.push({ field, code: 'TEXT_TOO_LONG' });
    if (text.split('\n').length > PRINT_FOOTER_MAX_LINES)
      problems.push({ field, code: 'TOO_MANY_LINES' });
    if (hasMarkup(text)) problems.push({ field, code: 'MARKUP_NOT_ALLOWED' });
  }

  return problems;
}
